import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getDashboardOverview, getDashboardVendors, scheduleVerificationScan } from '../../services/api';
import ScoreDistributionChart from '../ScoreDistributionChart';
import { trackGoogleAdsConversion } from '../../lib/googleAds';
import '../../styles/dashboard-pages.css';

const SUBPAGE_PATH_INPUT_REGEX = /^[A-Za-z0-9\-._~%!$&'()*+,;=:@/?#]*$/;

const metricChartConfig = {
  final_score: {
    label: 'Total Security Score',
    color: '#a0ffc3',
    glow: 'rgba(160, 255, 195, 0.32)'
  },
  transparency: {
    label: 'Transparency',
    color: '#feb700',
    glow: 'rgba(254, 183, 0, 0.28)'
  },
  external_integrity: {
    label: 'External Integrity',
    color: '#a0ffc3',
    glow: 'rgba(160, 255, 195, 0.32)'
  },
  policy_compliance: {
    label: 'Policy Compliance',
    color: '#a0ffc3',
    glow: 'rgba(160, 255, 195, 0.32)'
  }
};

function GlobalNetworkScore() {
  const [overview, setOverview] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredHistoryPoint, setHoveredHistoryPoint] = useState(null);
  const [displayedOrgScore, setDisplayedOrgScore] = useState(0);
  const [displayedOrgTransparency, setDisplayedOrgTransparency] = useState(0);
  const [displayedOrgPenaltyOutside, setDisplayedOrgPenaltyOutside] = useState(0);
  const [displayedOrgPenaltyPolicy, setDisplayedOrgPenaltyPolicy] = useState(0);
  const [selectedHistoryMetric, setSelectedHistoryMetric] = useState('final_score');
  const [vendorSearchFilter, setVendorSearchFilter] = useState('');
  const [vendorSortBy, setVendorSortBy] = useState('alphabetical');
  const [historyChartWidth, setHistoryChartWidth] = useState(960);
  const [subpagePath, setSubpagePath] = useState('');
  const [schedulingVerification, setSchedulingVerification] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const [verificationExpanded, setVerificationExpanded] = useState(true);
  const historyChartCanvasRef = useRef(null);

  const getTopPercentLabel = (percentile) => {
    if (percentile == null || Number.isNaN(Number(percentile))) return 'N/A';
    const topPercent = Math.max(1, 101 - Math.round(Number(percentile)));
    return `Top ${topPercent}%`;
  };

  const verificationLevel = overview?.verification_level || 'not_verified';
  const verificationLabel =
    verificationLevel === 'fully_verified'
      ? 'FULLY VERIFIED'
      : verificationLevel === 'stage_1_verified'
      ? 'STAGE 1 VERIFIED'
      : 'NOT VERIFIED';

  useEffect(() => {
    setVerificationExpanded(verificationLevel !== 'fully_verified');
  }, [verificationLevel]);

  useEffect(() => {
    if (verificationLevel === 'fully_verified') {
      trackGoogleAdsConversion('company_verification_completed', { once: true });
      trackGoogleAdsConversion('badge_enabled', { once: true });
    }
  }, [verificationLevel]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [overviewData, vendorsData] = await Promise.all([
          getDashboardOverview(),
          getDashboardVendors()
        ]);
        setOverview(overviewData);
        setVendors(vendorsData);
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
        setOverview({});
        setVendors([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!overview?.has_pending_verification_scan) return undefined;

    const intervalId = setInterval(async () => {
      try {
        const refreshedOverview = await getDashboardOverview();
        setOverview(refreshedOverview);
      } catch (_err) {
        // Best-effort refresh while verification is pending.
      }
    }, 15000);

    return () => clearInterval(intervalId);
  }, [overview?.has_pending_verification_scan]);

  const handleScheduleVerification = async (event) => {
    event.preventDefault();

    if (overview?.has_pending_verification_scan) {
      setVerificationMessage('You are scheduled for a scan soon.');
      return;
    }

    setSchedulingVerification(true);
    setVerificationError('');
    setVerificationMessage('');

    try {
      const trimmedSubpage = subpagePath.trim();

      if (/^https?:\/\//i.test(trimmedSubpage)) {
        setVerificationError('Please enter only a subpage path, not a full URL.');
        return;
      }

      if (!SUBPAGE_PATH_INPUT_REGEX.test(trimmedSubpage)) {
        setVerificationError('Subpage contains invalid characters.');
        return;
      }

      if (trimmedSubpage.length > 200) {
        setVerificationError('Subpage path is too long.');
        return;
      }

      const normalizedSubpage = trimmedSubpage.replace(/^\/+/, '');
      const pathForApi = normalizedSubpage ? `/${normalizedSubpage}` : '';
      const scan = await scheduleVerificationScan(pathForApi);

      setVerificationMessage(
        scan.already_scheduled
          ? 'You are scheduled for a scan soon.'
          : `Verification scan scheduled for ${scan.crawl_url}`
      );
      setOverview((previous) => ({
        ...(previous || {}),
        has_pending_verification_scan: true,
        pending_verification_crawl_url: scan.crawl_url,
      }));
    } catch (err) {
      setVerificationError(err?.response?.data?.error || 'Failed to schedule verification scan');
    } finally {
      setSchedulingVerification(false);
    }
  };

  // Animated organization score effect
  useEffect(() => {
    if (!overview?.final_score) return;

    let startValue = 0;
    const endValue = overview.final_score;
    const duration = 1200;
    const startTime = Date.now();
    let animationFrameId = null;
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const current = Math.floor(startValue + (endValue - startValue) * progress);
      setDisplayedOrgScore(current);
      
      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate);
      }
    };
    
    animate();

    return () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [overview?.final_score]);

  useEffect(() => {
    const updateChartWidth = () => {
      const nextWidth = historyChartCanvasRef.current?.clientWidth;
      if (nextWidth) {
        setHistoryChartWidth(Math.max(Math.floor(nextWidth), 320));
      }
    };

    updateChartWidth();

    if (typeof ResizeObserver !== 'undefined' && historyChartCanvasRef.current) {
      const observer = new ResizeObserver(() => {
        updateChartWidth();
      });

      observer.observe(historyChartCanvasRef.current);

      return () => {
        observer.disconnect();
      };
    }

    window.addEventListener('resize', updateChartWidth);
    return () => {
      window.removeEventListener('resize', updateChartWidth);
    };
  }, []);

  // Animated organization transparency effect
  useEffect(() => {
    if (overview?.transparency == null) return;

    let startValue = 0;
    const endValue = (parseFloat(overview.transparency) * 100);
    const duration = 1200;
    const startTime = Date.now();
    let animationFrameId = null;
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const current = Math.floor(startValue + (endValue - startValue) * progress);
      setDisplayedOrgTransparency(current);
      
      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate);
      }
    };
    
    animate();

    return () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [overview?.transparency]);

  // Animated organization penalty outside effect
  useEffect(() => {
    if (overview?.penalty_outside_in == null) return;

    let startValue = 0;
    const endValue = 100 - overview.penalty_outside_in;
    const duration = 1200;
    const startTime = Date.now();
    let animationFrameId = null;
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const current = Math.floor(startValue + (endValue - startValue) * progress);
      setDisplayedOrgPenaltyOutside(current);
      
      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate);
      }
    };
    
    animate();

    return () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [overview?.penalty_outside_in]);

  // Animated organization penalty policy effect
  useEffect(() => {
    if (overview?.penalty_policy == null) return;

    let startValue = 0;
    const endValue = 100 - overview.penalty_policy;
    const duration = 1200;
    const startTime = Date.now();
    let animationFrameId = null;
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const current = Math.floor(startValue + (endValue - startValue) * progress);
      setDisplayedOrgPenaltyPolicy(current);
      
      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate);
      }
    };
    
    animate();

    return () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [overview?.penalty_policy]);

  // Helper function to get verification color
  const getVerificationColor = (level) => {
    return level === 'fully_verified'
      ? '#00c853'
      : level === 'stage_1_verified'
      ? '#fbc02d'
      : '#e53935';
  };

  // Helper function to get verification label
  const getVerificationLabel = (level) => {
    return level === 'fully_verified'
      ? 'FULLY VERIFIED'
      : level === 'stage_1_verified'
      ? 'STAGE 1 VERIFIED'
      : 'NOT VERIFIED';
  };

  const getMetricScoreFromSnapshot = (item, metricId) => {
    if (!item) return null;

    switch (metricId) {
      case 'final_score':
        return item?.final_score ?? null;
      case 'transparency':
        return item?.transparency == null ? null : Number(item.transparency) * 100;
      case 'external_integrity': {
        const penaltyOutside = item?.penalty_outside_in;
        return penaltyOutside !== null && penaltyOutside !== undefined ? 100 - penaltyOutside : null;
      }
      case 'policy_compliance': {
        const penaltyPolicy = item?.penalty_policy;
        return penaltyPolicy !== null && penaltyPolicy !== undefined ? 100 - penaltyPolicy : null;
      }
      default:
        return null;
    }
  };

  // Main trend chart renderer
  const renderMetricHistoryChart = (historyData, metricId) => {
    const chartWidth = historyChartWidth;
    const chartHeight = 640;
    const chartPad = chartWidth < 560 ? 30 : 24;
    const metricConfig = metricChartConfig[metricId] || metricChartConfig.final_score;
    
    const history = Array.isArray(historyData) ? [...historyData].reverse() : [];

    const points = history
      .map((item, idx) => ({
        index: idx,
        score: getMetricScoreFromSnapshot(item, metricId),
        date: item?.snapshot_date || null
      }))
      .filter(item => item.score !== null);

    const maxVisiblePoints = Math.max(8, Math.floor((chartWidth - chartPad * 2) / 30));
    const visiblePoints = points.length > maxVisiblePoints ? points.slice(-maxVisiblePoints) : points;
    const hasData = visiblePoints.length > 0;
    const chartInnerHeight = chartHeight - chartPad * 2;
    const slotWidth = hasData ? (chartWidth - chartPad * 2) / visiblePoints.length : chartWidth - chartPad * 2;
    const barWidth = Math.max(Math.min(slotWidth - 3, 28), 6);
    const medianScore = hasData ? visiblePoints.map(point => point.score).sort((a, b) => a - b)[Math.floor(visiblePoints.length / 2)] : 50;
    const medianY = chartPad + (1 - (medianScore / 100)) * chartInnerHeight;

    const bars = hasData
      ? visiblePoints.map((point, idx) => {
          const normalized = point.score / 100;
          const height = Math.max(8, normalized * chartInnerHeight);
          const x = chartPad + idx * slotWidth + (slotWidth - barWidth) / 2;
          const y = chartHeight - chartPad - height;
          return {
            ...point,
            x,
            y,
            width: barWidth,
            height,
            isCurrent: idx === visiblePoints.length - 1
          };
        })
      : [];

    const hoveredPoint = hasData && hoveredHistoryPoint?.metricId === metricId
      ? bars.find(point => point.index === hoveredHistoryPoint.pointIndex)
      : null;
    const latestPoint = hasData ? bars[bars.length - 1] : null;
    const yAxisPad = 6;
    const gridLines = [0.16, 0.34, 0.52, 0.7, 0.88];

    const axisLabels = {
      left: visiblePoints[0]?.date ? new Date(visiblePoints[0].date).toLocaleDateString(undefined, { month: 'short', day: '2-digit' }) : 'Start',
      center: visiblePoints[Math.floor(visiblePoints.length / 2)]?.date ? new Date(visiblePoints[Math.floor(visiblePoints.length / 2)].date).toLocaleDateString(undefined, { month: 'short', day: '2-digit' }) : 'Mid',
      right: 'Today'
    };
    
    return (
      <div className="history-chart-shell">
        <div className="history-chart-canvas" ref={historyChartCanvasRef}>
          <svg 
            width="100%" 
            height={chartHeight} 
            viewBox={`0 0 ${chartWidth} ${chartHeight}`}
            preserveAspectRatio="none"
            aria-label={`${metricConfig.label} history chart`}
          >
            <defs>
              <linearGradient id={`history-bar-${metricId}`} x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor={metricConfig.color} stopOpacity="1" />
                <stop offset="100%" stopColor={metricConfig.color} stopOpacity="0.62" />
              </linearGradient>
              <filter id={`history-glow-${metricId}`} x="-40%" y="-40%" width="180%" height="180%">
                <feGaussianBlur stdDeviation="5" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            <rect x="0" y="0" width={chartWidth} height={chartHeight} rx="18" fill="#0a0e27" />

            {gridLines.map((ratio) => {
              const y = chartPad + (chartHeight - 2 * chartPad) * ratio;
              return (
                <line
                  key={ratio}
                  x1={chartPad}
                  y1={y}
                  x2={chartWidth - chartPad}
                  y2={y}
                  stroke="rgba(255,255,255,0.08)"
                  strokeWidth="1"
                  strokeDasharray="4 8"
                />
              );
            })}

            <line x1={chartPad} y1={chartPad} x2={chartPad} y2={chartHeight - chartPad} stroke="rgba(255,255,255,0.16)" strokeWidth="1" />
            <line x1={chartPad} y1={chartHeight - chartPad} x2={chartWidth - chartPad} y2={chartHeight - chartPad} stroke="rgba(255,255,255,0.16)" strokeWidth="1" />
            
            <text x={chartPad - yAxisPad} y={chartHeight - chartPad + 3} fontSize="8" fill="rgba(255,255,255,0.5)" textAnchor="end">0</text>
            <text x={chartPad - yAxisPad} y={chartPad + chartHeight / 2 + 2} fontSize="8" fill="rgba(255,255,255,0.5)" textAnchor="end">50</text>
            <text x={chartPad - yAxisPad} y={chartPad + 3} fontSize="8" fill="rgba(255,255,255,0.5)" textAnchor="end">100</text>

            {hasData && (
              <line
                x1={chartPad}
                y1={medianY}
                x2={chartWidth - chartPad}
                y2={medianY}
                stroke="rgba(169, 171, 179, 0.5)"
                strokeDasharray="6 6"
                strokeWidth="1"
              />
            )}

            {hoveredPoint && (
              <line
                x1={hoveredPoint.x + hoveredPoint.width / 2}
                y1={chartPad - 4}
                x2={hoveredPoint.x + hoveredPoint.width / 2}
                y2={chartHeight - chartPad + 4}
                stroke={metricConfig.color}
                strokeOpacity="0.35"
                strokeDasharray="3 6"
              />
            )}

            {bars.map((p) => {
              const isHovered = hoveredPoint?.index === p.index;
              const isLatest = !hoveredPoint && latestPoint && p.index === latestPoint.index;
              const tooltipAbove = p.y - 34;
              const tooltipBelow = p.y + 10;
              const shouldPlaceBelow = tooltipAbove < chartPad + 8;
              const tooltipY = shouldPlaceBelow ? tooltipBelow : tooltipAbove;
              const tooltipTextY = tooltipY + 16;
              const tooltipX = p.x + p.width / 2;

              return (
                <g key={`${metricId}-${p.index}`}>
                  <rect
                    x={p.x}
                    y={p.y}
                    width={p.width}
                    height={p.height}
                    rx="3"
                    fill={p.isCurrent ? `url(#history-bar-${metricId})` : 'rgba(35, 44, 56, 0.75)'}
                    opacity={isHovered ? '1' : p.isCurrent ? '0.95' : '0.72'}
                    filter={p.isCurrent || isHovered || isLatest ? `url(#history-glow-${metricId})` : undefined}
                    style={{ cursor: 'pointer', transition: 'all 180ms ease' }}
                    onMouseEnter={() => setHoveredHistoryPoint({ metricId, pointIndex: p.index })}
                    onMouseLeave={() => setHoveredHistoryPoint((current) => (
                      current?.metricId === metricId && current?.pointIndex === p.index ? null : current
                    ))}
                  />
                  <rect
                    x={p.x}
                    y={chartPad}
                    width={p.width}
                    height={chartHeight - chartPad * 2}
                    fill="transparent"
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={() => setHoveredHistoryPoint({ metricId, pointIndex: p.index })}
                    onMouseLeave={() => setHoveredHistoryPoint((current) => (
                      current?.metricId === metricId && current?.pointIndex === p.index ? null : current
                    ))}
                  />

                  {isHovered && (
                    <g>
                      <rect
                        x={tooltipX - 30}
                        y={tooltipY}
                        width="60"
                        height="24"
                        rx="4"
                        fill="rgba(9, 13, 21, 0.95)"
                      />
                      <text
                        x={tooltipX}
                        y={tooltipTextY}
                        fontSize="11"
                        fill="#ecedf6"
                        textAnchor="middle"
                        fontWeight="600"
                      >
                        {Math.round(p.score)}
                      </text>
                    </g>
                  )}
                </g>
              );
            })}

            {!hasData && (
              <g opacity="0.28">
                {Array.from({ length: 10 }).map((_, idx) => {
                  const columnWidth = (chartWidth - chartPad * 2) / 10;
                  const x = chartPad + idx * columnWidth + columnWidth * 0.22;
                  const height = [120, 90, 140, 100, 150, 110, 130, 95, 145, 115][idx];
                  const y = chartHeight - chartPad - height;
                  return (
                    <rect
                      key={`empty-bar-${idx}`}
                      x={x}
                      y={y}
                      width={columnWidth * 0.56}
                      height={height}
                      rx="3"
                      fill="rgba(160, 255, 195, 0.08)"
                    />
                  );
                })}
              </g>
            )}
          </svg>
        </div>

        <div className="history-axis-labels">
          <span>{axisLabels.left}</span>
          <span>{axisLabels.center}</span>
          <span>{axisLabels.right}</span>
        </div>
      </div>
    );
  };

  const orgName = overview?.name || 'Your Organization';
  const historyMetricItems = [
    {
      metricId: 'final_score',
      label: 'Total Security Score',
      value: overview?.final_score == null ? 'N/A' : displayedOrgScore,
      suffix: '/100',
      detail: 'Overall trend'
    },
    {
      metricId: 'external_integrity',
      label: 'External Integrity',
      value: overview?.penalty_outside_in == null ? 'N/A' : displayedOrgPenaltyOutside,
      suffix: '/100',
      detail: 'Outside factors'
    },
    {
      metricId: 'policy_compliance',
      label: 'Policy Compliance',
      value: overview?.penalty_policy == null ? 'N/A' : displayedOrgPenaltyPolicy,
      suffix: '/100',
      detail: 'Policy adherence'
    },
    {
      metricId: 'transparency',
      label: 'Transparency',
      value: overview?.transparency == null ? 'N/A' : displayedOrgTransparency,
      suffix: '%',
      detail: 'Visibility quality',
      hasHistory: false
    }
  ];

  const historySeries = Array.isArray(overview?.score_history) ? [...overview.score_history].reverse() : [];

  const getMetricSparklineHeights = (metricId) => {
    const values = historySeries
      .map((item) => getMetricScoreFromSnapshot(item, metricId))
      .filter((value) => value !== null)
      .slice(-6);

    if (values.length === 0) return [42, 48, 45, 55, 52, 60];

    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = Math.max(max - min, 1);

    return values.map((value) => {
      const normalized = (value - min) / range;
      return Math.round(26 + normalized * 54);
    });
  };
  const hasStaleScoreData = Boolean(overview?.score_data_stale);
  const companyDomain = overview?.domain || '';
  const hasCompanyDomain = Boolean(companyDomain);
  const isFullyVerified = verificationLevel === 'fully_verified';
  const hasPendingVerificationScan = Boolean(overview?.has_pending_verification_scan);
  const staleScoreSinceLabel = overview?.score_data_stale_since
    ? new Date(overview.score_data_stale_since).toLocaleString()
    : null;

  return (
    <div className="global-network-page">
      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center' }}>Loading dashboard...</div>
      ) : (
        <>
          {hasStaleScoreData && (
            <div className="stale-data-notice" role="status" style={{ marginBottom: '18px' }}>
              <span className="material-symbols-outlined">schedule</span>
              <div>
                <p className="stale-data-title">Scores are being recalculated</p>
                <p className="stale-data-text">
                  Recent dependency changes were detected, so displayed values may be temporarily outdated.
                  {staleScoreSinceLabel ? ` Pending since ${staleScoreSinceLabel}.` : ''}
                </p>
              </div>
            </div>
          )}

          <section
            className={`insights-section verification-scan-section global-verification-section ${
              hasPendingVerificationScan ? 'is-disabled' : ''
            } ${verificationExpanded ? '' : 'is-collapsed'}`}
            data-tour="verification-check"
          >
            <div className="global-verification-heading">
              <div>
                <span className="global-verification-kicker">
                  {isFullyVerified ? 'Company verification' : 'Recommended first step'}
                </span>
                <h2>{isFullyVerified ? 'Verification complete' : 'Get Fully Verified'}</h2>
              </div>
              <div className="global-verification-actions">
                <span className={`global-verification-status ${isFullyVerified ? 'is-complete' : ''}`}>
                  {isFullyVerified ? 'Fully verified' : verificationLabel}
                </span>
                <button
                  type="button"
                  className="global-verification-toggle"
                  aria-expanded={verificationExpanded}
                  aria-label={verificationExpanded ? 'Collapse verification section' : 'Expand verification section'}
                  onClick={() => setVerificationExpanded((current) => !current)}
                >
                  <span className="material-symbols-outlined">
                    {verificationExpanded ? 'expand_less' : 'expand_more'}
                  </span>
                </button>
              </div>
            </div>

            {verificationExpanded && (
              isFullyVerified ? (
                <div className="global-verification-complete">
                  <span className="material-symbols-outlined">verified</span>
                  <p>Your website link has been verified and your company has full verification status.</p>
                </div>
              ) : (
                <>
                  <p className="verification-scan-description">
                    Add a link to your Risqly company page on your website, then schedule a crawl here to
                    confirm ownership and become fully verified.
                  </p>
                  <p className="verification-guide-link">
                    <Link to="/blog/how-to-pass-risqly-domain-verification">
                      Help: How to get fully verified (including AI prompt)
                    </Link>
                  </p>
                  {hasPendingVerificationScan && (
                    <div className="verification-scan-pending-notice">
                      You are scheduled for a scan soon.
                      {overview?.pending_verification_crawl_url ? ` (${overview.pending_verification_crawl_url})` : ''}
                    </div>
                  )}
                  <form className="verification-scan-form" onSubmit={handleScheduleVerification}>
                    <label htmlFor="verification-subpage" className="verification-scan-label">
                      Page containing your Risqly profile link
                    </label>
                    <div className="verification-url-row">
                      <span className="verification-url-prefix">https://{companyDomain}/</span>
                      <input
                        id="verification-subpage"
                        type="text"
                        value={subpagePath}
                        onChange={(event) => setSubpagePath(event.target.value)}
                        className="verification-scan-input"
                        placeholder="about/security"
                        maxLength={200}
                        disabled={schedulingVerification || !hasCompanyDomain || hasPendingVerificationScan}
                      />
                    </div>
                    <p className="verification-scan-help">
                      Leave empty if the link is on your homepage. Enter only the path when it is on a subpage.
                    </p>
                    {!hasCompanyDomain && (
                      <p className="verification-scan-error">
                        Your company domain is missing, so verification cannot be scheduled yet.
                      </p>
                    )}
                    <button
                      type="submit"
                      className="verification-scan-button"
                      disabled={schedulingVerification || !hasCompanyDomain || hasPendingVerificationScan}
                    >
                      {hasPendingVerificationScan
                        ? 'Scan already scheduled'
                        : schedulingVerification
                          ? 'Scheduling...'
                          : 'Schedule verification scan'}
                    </button>
                  </form>
                  {verificationMessage && <p className="verification-scan-success">{verificationMessage}</p>}
                  {verificationError && <p className="verification-scan-error">{verificationError}</p>}
                </>
              )
            )}
          </section>

          {/* KPI Cards */}
          <div className="kpi-grid">
            <div className="kpi-card">
              <div className="kpi-label">Declared Vendors</div>
              <div className="kpi-value">{vendors?.length}</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-label">Security Percentile</div>
              <div className="kpi-value">{getTopPercentLabel(overview?.final_score_percentile)}</div>
            </div>
          </div>

          <section className="dashboard-distribution-section" aria-label="Dashboard score distributions">
            <div className="section-title">
              <h2>Score Distribution Across The Network</h2>
            </div>

            <div className="dashboard-distribution-grid">
              <ScoreDistributionChart
                title="Total Security Score"
                description="Your Total Security Score compared to every scored company in the current network."
                score={overview?.final_score ?? null}
                percentile={overview?.final_score_percentile}
                buckets={overview?.score_distributions?.security_score}
                accentColor="#a0ffc3"
                showAxisLabels
              />

              <ScoreDistributionChart
                title="External Integrity"
                description="Distribution of external technical resilience and exposed risk signals."
                score={overview?.penalty_outside_in == null ? null : 100 - overview.penalty_outside_in}
                percentile={overview?.external_integrity_percentile}
                buckets={overview?.score_distributions?.external_integrity}
                accentColor="#f58ab5"
                showAxisLabels
              />

              <ScoreDistributionChart
                title="Policy Compliance"
                description="Distribution of policy governance quality and security control coverage."
                score={overview?.penalty_policy == null ? null : 100 - overview.penalty_policy}
                percentile={overview?.policy_compliance_percentile}
                buckets={overview?.score_distributions?.policy_compliance}
                accentColor="#7fe8ff"
                showAxisLabels
              />
            </div>
          </section>

          {/* Company Score Card */}
          <div className="company-score-section">
            <div className="section-title">
              <h2>Your Organization: <span style={{ color: 'var(--primary)' }}>{orgName}</span></h2>
            </div>
            <div className="company-score-card">
              <div className="intel-layout" style={{ animation: 'slideInUp 0.6s ease-out 0.25s both' }}>
                <div className="intel-metric-rail" role="tablist" aria-label="Security intelligence metrics">
                  {historyMetricItems.map((item) => {
                    const isSelectable = item.hasHistory !== false;
                    const isActive = isSelectable && selectedHistoryMetric === item.metricId;
                    const sparkHeights = isSelectable ? getMetricSparklineHeights(item.metricId) : [];

                    return (
                      <button
                        key={item.metricId}
                        type="button"
                        className={`intel-metric-card${isActive ? ' is-active' : ''}${!isSelectable ? ' is-disabled' : ''}`}
                        aria-pressed={isActive}
                        aria-disabled={!isSelectable}
                        disabled={!isSelectable}
                        onClick={() => {
                          if (isSelectable) {
                            setSelectedHistoryMetric(item.metricId);
                          }
                        }}
                      >
                        <div className="intel-metric-header">
                          <span className="intel-metric-label">{item.label}</span>
                        </div>
                        <div className="intel-metric-value-row">
                          <strong className="intel-metric-value">{item.value}</strong>
                          <span className="intel-metric-suffix">{item.suffix}</span>
                        </div>
                        <p className="intel-metric-detail">{item.detail}</p>
                        <div className="intel-metric-sparkline" aria-hidden="true">
                          {isSelectable && sparkHeights.map((height, idx) => (
                            <span
                              key={`${item.metricId}-spark-${idx}`}
                              className="intel-spark-bar"
                              style={{ height: `${height}%` }}
                            />
                          ))}
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="org-history-card">
                <div className="org-history-header">
                  <h3>{metricChartConfig[selectedHistoryMetric]?.label || 'Total Security Score'} Trend</h3>
                  <div className="org-history-legend">
                    <span><i className="trend-dot is-current" />Latest Snapshot</span>
                    <span><i className="trend-dot is-median" />Historical Median</span>
                  </div>
                </div>
                {renderMetricHistoryChart(overview?.score_history, selectedHistoryMetric)}
                </div>
              </div>
            </div>
          </div>

          {/* Vendors Section */}
          <div className="vendors-section">
            <div className="section-header vendors-section-header">
              <h2>
                <span className="material-symbols-outlined">domain</span>
                Your Declared Vendors
              </h2>
              <div className="vendors-controls">
                <div className="search-box">
                  <span className="material-symbols-outlined">search</span>
                  <input 
                    type="text" 
                    placeholder="Filter vendors..." 
                    value={vendorSearchFilter}
                    onChange={(e) => setVendorSearchFilter(e.target.value)}
                  />
                </div>
                <div className="sort-box">
                  <label htmlFor="vendor-sort">Sort by:</label>
                  <select 
                    id="vendor-sort"
                    value={vendorSortBy}
                    onChange={(e) => setVendorSortBy(e.target.value)}
                  >
                    <option value="alphabetical">Alphabetical</option>
                    <option value="effective_penalty">Effective Penalty</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="vendors-grid">
              {(() => {
                // Filter vendors
                let filtered = vendors.filter(vendor => 
                  vendor.vendor_name.toLowerCase().includes(vendorSearchFilter.toLowerCase())
                );

                // Sort vendors
                if (vendorSortBy === 'effective_penalty') {
                  filtered.sort((a, b) => {
                    const penaltyA = a.effective_penalty ?? 0;
                    const penaltyB = b.effective_penalty ?? 0;
                    return penaltyB - penaltyA; // Higher penalty first
                  });
                } else {
                  filtered.sort((a, b) => a.vendor_name.localeCompare(b.vendor_name));
                }

                return filtered.length === 0 ? (
                  <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px', opacity: 0.6 }}>
                    <span className="material-symbols-outlined" style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>search_off</span>
                    <p>No vendors found</p>
                  </div>
                ) : (
                  filtered.map((vendor, idx) => {
                    const vendorExternalScore =
                      vendor.penalty_outside_in == null ? null : 100 - vendor.penalty_outside_in;
                    const vendorPolicyScore =
                      vendor.penalty_policy == null ? null : 100 - vendor.penalty_policy;

                    return (
                    <div key={vendor.id} className="vendor-card" style={{ animation: `slideInUp 0.6s ease-out ${0.2 + idx * 0.15}s both` }}>
                      <h3>{vendor.vendor_name}</h3>
                      {vendor.final_score == null ? (
                        <div className="vendor-no-data">
                          <span className="material-symbols-outlined" style={{ fontSize: '32px', marginBottom: '8px', opacity: 0.5 }}>info</span>
                          <p>No security score available yet. This vendor has not been assessed.</p>
                        </div>
                      ) : (
                        <div className="vendor-metrics-grid">
                          <div className="vendor-metric-card">
                            <div className="vendor-score-circle">
                              <svg viewBox="0 0 160 160">
                                <circle cx="80" cy="80" r="70" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="6" />
                                <circle 
                                  cx="80" 
                                  cy="80" 
                                  r="70" 
                                  fill="none" 
                                  stroke="#00ff41" 
                                  strokeWidth="6" 
                                  strokeDasharray="440" 
                                  strokeDashoffset={440 - (vendor.final_score / 100) * 440} 
                                  strokeLinecap="round"
                                />
                              </svg>
                              <div className="vendor-circle-content">
                                <span className="vendor-circle-value">{vendor.final_score ?? 0}</span>
                                <span className="vendor-circle-max">/ 100</span>
                              </div>
                            </div>
                            <p className="vendor-metric-label">Total Security Score</p>
                          </div>

                          <div className="vendor-metric-card">
                            <div className="vendor-score-circle">
                              <svg viewBox="0 0 160 160">
                                <circle cx="80" cy="80" r="70" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="6" />
                                <circle 
                                  cx="80" 
                                  cy="80" 
                                  r="70" 
                                  fill="none" 
                                  stroke="#ff9500" 
                                  strokeWidth="6" 
                                  strokeDasharray="440" 
                                  strokeDashoffset={440 - ((vendor.transparency * 100) / 100) * 440} 
                                  strokeLinecap="round"
                                />
                              </svg>
                              <div className="vendor-circle-content">
                                <span className="vendor-circle-value">{vendor.transparency == null ? 'N/A' : `${Math.round(vendor.transparency * 100)}%`}</span>
                              </div>
                            </div>
                            <p className="vendor-metric-label">Transparency</p>
                          </div>

                          <div className="vendor-metric-card">
                            <div className="vendor-score-circle">
                              <svg viewBox="0 0 160 160">
                                <circle cx="80" cy="80" r="70" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="6" />
                                <circle 
                                  cx="80" 
                                  cy="80" 
                                  r="70" 
                                  fill="none" 
                                  stroke="#e91e63" 
                                  strokeWidth="6" 
                                  strokeDasharray="440" 
                                  strokeDashoffset={
                                    vendorExternalScore == null
                                      ? 440
                                      : 440 - (vendorExternalScore / 100) * 440
                                  }
                                  strokeLinecap="round"
                                />
                              </svg>
                              <div className="vendor-circle-content">
                                <span className="vendor-circle-value">
                                  {vendorExternalScore == null ? 'N/A' : vendorExternalScore}
                                </span>
                                {vendorExternalScore != null && <span className="vendor-circle-max">/ 100</span>}
                              </div>
                            </div>
                            <p className="vendor-metric-label">External Integrity</p>
                          </div>

                          <div className="vendor-metric-card">
                            <div className="vendor-score-circle">
                              <svg viewBox="0 0 160 160">
                                <circle cx="80" cy="80" r="70" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="6" />
                                <circle 
                                  cx="80" 
                                  cy="80" 
                                  r="70" 
                                  fill="none" 
                                  stroke="#00bcd4" 
                                  strokeWidth="6" 
                                  strokeDasharray="440" 
                                  strokeDashoffset={
                                    vendorPolicyScore == null
                                      ? 440
                                      : 440 - (vendorPolicyScore / 100) * 440
                                  }
                                  strokeLinecap="round"
                                />
                              </svg>
                              <div className="vendor-circle-content">
                                <span className="vendor-circle-value">
                                  {vendorPolicyScore == null ? 'N/A' : vendorPolicyScore}
                                </span>
                                {vendorPolicyScore != null && <span className="vendor-circle-max">/ 100</span>}
                              </div>
                            </div>
                            <p className="vendor-metric-label">Policy Compliance</p>
                          </div>
                        </div>
                      )}

                      <div className="vendor-footer">
                        <div className="vendor-footer-left">
                          <span className="vendor-importance">Importance: {vendor.importance_weight}</span>
                          <div className="vendor-status" style={{ color: getVerificationColor(vendor.verification_level) }}>
                            <div style={{ 
                              width: '10px', 
                              height: '10px', 
                              borderRadius: '50%', 
                              backgroundColor: getVerificationColor(vendor.verification_level),
                              flexShrink: 0
                            }}></div>
                            <span className="vendor-status-label" style={{ color: getVerificationColor(vendor.verification_level), fontSize: '11px' }}>
                              {getVerificationLabel(vendor.verification_level)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                  })
                );
              })()}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default GlobalNetworkScore;
